<template>
  <div>
    <b-alert v-if="initialized && materialLists.length === 0" variant="warning" show>
      <strong>Achtung:</strong> Es wurde noch keine Materiallisten für diese Fahrt hinterlegt! Normalerweise sollte für
      eine Fahrt immer eine für die Unterkunft und/oder die Art der Fahrt passende Materialliste ausgewählt werden.
    </b-alert>

    <b-row class="mb-2">
      <b-col cols="12" sm="8">
        <strong>Materiallisten:</strong>
        <span v-for="(list, index) in materialLists">
          <a :href="list.url">
            {{ list.text }}<span v-if="index+1 < materialLists.length">, </span>
          </a>
        </span>
        <span v-if="initialized && materialLists.length === 0">Es wurden keine Materiallisten ausgewählt, <a :href="tripEditUrl">ändern</a>.</span>
      </b-col>
      <b-col cols="12" sm="4">
        <b-btn-group class="float-right">
          <b-btn size="sm" variant="outline-primary" @click="fetchData()" :disabled="isBusy" >
            <i class="fas fa-sync-alt"></i> Aktualisieren
          </b-btn>
          <b-btn size="sm" variant="outline-success" v-b-modal.item-add-dialog :disabled="isBusy">
            <i class="fas fa-plus"></i> Hinzufügen
          </b-btn>
        </b-btn-group>
        <AddItemDialog :items="items" :filter-options="filterOptions" :api-url="apiUrl" />
      </b-col>
    </b-row>

    <b-table
        striped
        hover
        :busy.sync="isBusy"
        :items="filteredItems"
        :fields="fields"
        :sort-compare="sortCompare"
        :tbody-tr-class="rowClass"
        stacked="sm"
        small
        bordered
        head-variant="dark"
    >
      <template slot="top-row">
        <td id="shopping-place-filter">
          <b-form-select size="sm" v-model="filters.shopping_place" :options="filterOptions.shopping_places">
            <template #first>
              <b-form-select-option value="">---</b-form-select-option>
            </template>
          </b-form-select>
        </td>
        <td class="d-none d-md-table-cell">
          <b-form-select size="sm" v-model="filters.ingredient_category" :options="filterOptions.ingredient_categories">
            <template #first>
              <b-form-select-option value="">---</b-form-select-option>
            </template>
          </b-form-select>
        </td>
        <td id="ingredient-filter">
          <b-form-input size="sm" v-model="filters.ingredient_title" placeholder="Zutat suchen..." debounce="200"></b-form-input>
        </td>
        <td class="d-none d-sm-table-cell"><b-form-input size="sm" v-model="filters.amount_needed" debounce="200"></b-form-input></td>
        <td class="d-none d-sm-table-cell"><b-form-input size="sm" v-model="filters.base_unit_title" debounce="200"></b-form-input></td>
        <td class="d-none d-lg-table-cell">
          <b-form-select size="sm" v-model="filters.ingredient_perishable" :options="filterOptions.ingredient_perishable">
            <template #first>
              <b-form-select-option value="">---</b-form-select-option>
            </template>
          </b-form-select>
        </td>
        <td class="d-none d-lg-table-cell">
          <b-form-select size="sm" v-model="filters.customs_category" :options="filterOptions.customs_categories">
            <template #first>
              <b-form-select-option value="">---</b-form-select-option>
            </template>
          </b-form-select>
        </td>
        <td class="d-none d-sm-table-cell">&nbsp;</td>
      </template>
      <template v-slot:cell(shopping_place)="{ item }">
        <b-btn-group class="float-right d-sm-none">
          <b-btn class="save-row-toggle" v-if="item.is_shopped" @click="toggleIsShopped(item)" size="xs" variant="primary" :disabled="item._updating">
            <i v-if="!item._updating" class="fas fa-undo" />
            <b-spinner v-if="item._updating" small variant="light"></b-spinner>
          </b-btn>
          <b-btn class="save-row-toggle" v-if="!item.is_shopped" @click="toggleIsShopped(item)" size="xs" variant="success" :disabled="item._updating">
            <i v-if="!item._updating" class="fas fa-check" />
            <b-spinner v-if="item._updating" small variant="light"></b-spinner>
          </b-btn>
          <b-btn @click="toggleDetails(item)" size="xs">
            <i class="fas fa-edit"></i>
          </b-btn>
        </b-btn-group>
        {{ formatShoppingPlace(item.shopping_place) }}
        <span class="d-md-none">({{ formatIngredientCategory(item.ingredient_category) }})</span>
      </template>
      <template v-slot:cell(ingredient_title)="{ item }">
        <div class="float-right d-sm-none">
          <span v-if="item.amount_needed !== item.amount_shopping" class="item-original-amount">
            ({{ formatAmountNeeded(item.amount_needed, 'amount_needed', item) }})</span>
          {{ formatAmountNeeded(item.amount_shopping, 'amount_shopping', item) }}
          {{ item.base_unit_title }}
        </div>
        {{ item.ingredient_title }}
      </template>
      <template v-slot:cell(amount_needed)="{ item }">
        <div v-if="item.amount_needed !== item.amount_shopping" class="float-left item-original-amount">
          ({{ formatAmountNeeded(item.amount_needed, 'amount_needed', item) }})
        </div>
        {{ formatAmountNeeded(item.amount_shopping, 'amount_needed', item) }}
      </template>
      <template v-slot:cell(actions)="{ item }">
        <b-btn-group>
          <b-btn class="save-row-toggle" v-if="item.is_shopped && canEdit" @click="toggleIsShopped(item)" size="xs" variant="primary" :disabled="item._updating">
            <i v-if="!item._updating" class="fas fa-undo" />
            <b-spinner v-if="item._updating" small variant="light"></b-spinner>
          </b-btn>
          <b-btn class="save-row-toggle" v-if="!item.is_shopped && canEdit" @click="toggleIsShopped(item)" size="xs" variant="success" :disabled="item._updating">
            <i v-if="!item._updating" class="fas fa-check" />
            <b-spinner v-if="item._updating" small variant="light"></b-spinner>
          </b-btn>
          <b-btn @click="toggleDetails(item)" size="xs">
            <i class="fas fa-edit"></i>
          </b-btn>
        </b-btn-group>
      </template>
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>Lade Einkaufsliste...</strong>
        </div>
      </template>
      <template #empty>
        <h4>Die Einkaufliste ist leer.</h4>
      </template>
      <template v-slot:row-details="{ item }">
        <ItemDetailsComponent :item="item" :filter-options="filterOptions" :can-edit="canEdit" :api-url="apiUrl"/>
      </template>
    </b-table>
  </div>
</template>

<script>
import HelperMixin from "./HelperMixin"
import ItemDetailsComponent from "./ItemDetailsComponent"
import AddItemDialog from "./AddItemDialog"
import Pusher from "pusher-js"
const axios = require('axios')

export default {
  components: { ItemDetailsComponent, AddItemDialog },
  mixins: [ HelperMixin ],
  data() {
    return {
      apiUrl: this.$parent.apiUrl,
      tripEditUrl: this.$parent.tripEditUrl,
      canEdit: this.$parent.canEdit,
      pusher: this.$parent.pusher,
      initialized: false,
      filterOptions: {
        'shopping_places': [],
        'ingredients': [],
        'ingredient_categories': [],
        'customs_categories': [],
        'ingredient_perishable': [
          { 'value': 0, 'text': 'Nein' },
          { 'value': 1, 'text': 'Ja' },
        ],
      },
      filters: {
        'shopping_place': '',
        'ingredient_category': '',
        'ingredient_title': '',
        'amount_needed': '',
        'base_unit_title': '',
        'ingredient_perishable': '',
        'customs_category': '',
      },
      fields: [
        { key: 'shopping_place', label: 'Einkaufsort', sortable: true, sortByFormatted: true, formatter: 'formatShoppingPlace' },
        { key: 'ingredient_category', label: 'Kategorie', sortable: true, sortByFormatted: true, formatter: 'formatIngredientCategory', thClass: 'd-none d-md-table-cell', tdClass: 'd-none d-md-table-cell' },
        { key: 'ingredient_title', label: 'Zutat', sortable: true },
        { key: 'amount_needed', label: 'Menge', formatter: 'formatAmountNeeded', thClass: 'text-right', tdClass: 'text-right d-none d-sm-table-cell' },
        { key: 'base_unit_title', label: 'Einheit', tdClass: 'd-none d-sm-table-cell' },
        { key: 'ingredient_perishable', label: 'Verderblich', formatter: 'formatIngredientPerishable', thClass: 'd-none d-lg-table-cell', tdClass: 'd-none d-lg-table-cell' },
        { key: 'customs_category', label: 'Zoll', sortable: true, sortByFormatted: true, formatter: 'formatCustomsCategory', thClass: 'd-none d-lg-table-cell', tdClass: 'd-none d-lg-table-cell' },
        { key: 'actions', label: '', tdClass: 'd-none d-sm-table-cell' },
      ],
      items: [],
      materialLists: [],
      isBusy: false
    }
  },
  computed: {
    filteredItems() {
      return this.items.filter(item => {
        return Object.keys(this.filters).every(key => {
          if (this.filters[key] === '') {
            return true;
          }
          if (Number.isInteger(item[key])) {
            return item[key] === this.filters[key];
          }
          return String(item[key]).toLowerCase().includes(this.filters[key].toLowerCase());
        })
      })
    }
  },
  mounted() {
    this.fetchData()
    let pusher = new Pusher(this.pusher.key, { cluster: 'eu' })
    let channel = pusher.subscribe(this.pusher.channel)

    channel.bind('recipes-changed', (data) => {
      console.log('[Pusher] received recipes-changed event', data)
      this.fetchData()
    })

    channel.bind('shopping-list-item-updated', (data) => {
      console.log('[Pusher] received shopping-list-item-updated event', data)
      let item = this.items.find(e => {
        return e.ingredient_id === data.ingredient_id
      })
      if (item) {
        item.amount_needed = data.amount_needed
        item.amount_shopping = data.amount_shopping
        item.amount_available = data.amount_available
        item.amount_delta = data.amount_delta
        item.is_shopped = data.is_shopped
      } else {
        this.items.push(data)
      }
    })
  },
  methods: {
    fetchData() {
      this.isBusy = true
      let promise = axios.get(this.apiUrl);

      return promise.then((result) => {
        this.items = result.data.shoppingList
        this.materialLists = result.data.materialLists
        this.filterOptions.shopping_places = result.data.shoppingPlaces
        this.filterOptions.ingredients = result.data.ingredients
        this.filterOptions.ingredient_categories = result.data.ingredientCategories
        this.filterOptions.customs_categories = result.data.customsCategories
        this.isBusy = false
        this.initialized = true
      }).catch(error => {
        this.isBusy = false
      })
    },
    toggleDetails(item) {
      if (item._showDetails) { // if details are open, close them
        item._showDetails = false
      } else {
        this.$set(item, '_showDetails', true)
      }
    },
    toggleIsShopped(item) {
      this.$set(item, '_updating', true)
      axios.put(this.apiUrl, {
        is_shopped: !item.is_shopped,
      }, {
        params: {
          id: item.ingredient_id,
        }
      }).then((result) => {
        this.$set(item, '_updating', false)
        this.$set(item, 'is_shopped', result.data.is_shopped)
      }).catch(error => {
        // maybe handle errors somehow?
      })
    },
    sortCompare(aRow, bRow, key, sortDesc, formatter, compareOptions, compareLocale) {
      let aValue = formatter ? formatter(aRow[key]) : aRow[key]
      let bValue = formatter ? formatter(bRow[key]) : bRow[key]
      if (key === 'shopping_place') {
        aValue += '|' + this.formatIngredientCategory(aRow['ingredient_category'])
        aValue += '|' + aRow['ingredient_title']
        bValue += '|' + this.formatIngredientCategory(bRow['ingredient_category'])
        bValue += '|' + bRow['ingredient_title']
      } else if (key === 'ingredient_category' || key === 'customs_category') {
        aValue += '|' + aRow['ingredient_title']
        bValue += '|' + bRow['ingredient_title']
      }
      return aValue.localeCompare(bValue, compareLocale, compareOptions)
    },
    rowClass(item, type) {
      if (!item || type !== 'row') return
      if (item.is_shopped) return 'table-success'
      if (item.amount_shopping <= 0) return 'table-warning'
    }
  }
}
</script>

<style module>
  @media (max-width: 575.98px) {
    .table.b-table .input-group-text {
      line-height: .9rem;
    }

    .table.b-table.b-table-stacked-sm > tbody > tr > [data-label]::before {
      width: 0 !important;
      content: "";
      background: blue;
    }

    .table.b-table.b-table-stacked-sm > tbody > tr > [data-label] > div {
      width: 100% !important;
      padding: 0;
    }

    #shopping-place-filter::before,
    #ingredient-filter::before {
      content: "Einkaufsort:";
      font-weight: bold;
      width: 30%;
      float: left;
      padding: .2rem .5rem .2rem 0;
    }
    #shopping-place-filter::before {
      content: "Einkaufsort:";
    }
    #ingredient-filter::before {
      content: "Zutat:";
    }
    #shopping-place-filter > *,
    #ingredient-filter > * {
      width: 70%;
      display: inline-block;
    }

    .save-row-toggle {
      width: 3rem !important;
      line-height: 1.2rem;
    }
  }

  .table.b-table td span {
    line-height: 1.5rem;
  }
  .table.b-table td .multiselect span {
    line-height: 1rem;
  }

  .item-original-amount {
    text-decoration: line-through;
    color: #999;
  }

  .save-row-toggle {
    width: 2rem;
  }

  tr.no-shopping > td div {
    text-decoration: line-through;
    color: #999;
  }
</style>